import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useConnectionStatus } from '../hooks/useConnectionStatus';
import { useAuth } from '../hooks/useAuth';
import type { ConnectionHealth } from '../types';
import {
  Avatar,
  BrandMark,
  ChevronDownIcon,
  KeyboardIcon,
  LogOutIcon,
  SettingsIcon,
  StatusPill,
  CheckIcon,
} from './primitives';

type Presence = 'on' | 'away' | 'off';

interface HeaderProps {
  onOpenSettings: () => void;
}

function combinedConnection(backend: ConnectionHealth, livekit: ConnectionHealth): ConnectionHealth {
  if (backend === 'disconnected') return 'disconnected';
  if (backend === 'connecting' || livekit === 'connecting') return 'connecting';
  if (backend === 'connected') return 'connected';
  return 'disconnected';
}

export default function Header({ onOpenSettings }: HeaderProps) {
  const { backend, livekit } = useConnectionStatus();
  const { logout, session, hasCapability } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = hasCapability('admin');
  const inAdmin = location.pathname.startsWith('/admin');

  const operatorName =
    session?.user.displayName?.trim() || session?.user.username || 'Operator';
  const operatorEmail = session?.user.username || '';
  const operatorRole = isAdmin ? 'Admin' : 'Operator';

  const [presence, setPresence] = useState<Presence>('on');
  const [presenceOpen, setPresenceOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);

  const presenceRef = useRef<HTMLDivElement | null>(null);
  const userRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (presenceRef.current && !presenceRef.current.contains(event.target as Node)) {
        setPresenceOpen(false);
      }
      if (userRef.current && !userRef.current.contains(event.target as Node)) {
        setUserOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const connection = combinedConnection(backend, livekit);
  const connectionTone =
    connection === 'connected' ? 'ok' : connection === 'connecting' ? 'warn' : 'error';
  const connectionLabel =
    connection === 'connected' ? 'Connected' : connection === 'connecting' ? 'Reconnecting' : 'Disconnected';
  const connectionTitle = `Backend: ${backend} · LiveKit: ${livekit}`;

  return (
    <header className="topbar" role="banner">
      <Link to="/" className="topbar__brand" aria-label="A.R.E.S home">
        <BrandMark size={28} />
      </Link>

      <nav className="topbar__nav" aria-label="Primary">
        <button
          type="button"
          className={`nav-item${!inAdmin ? ' is-active' : ''}`}
          onClick={() => navigate('/')}
        >
          Home
        </button>
        {isAdmin && (
          <button
            type="button"
            className={`nav-item${inAdmin ? ' is-active' : ''}`}
            onClick={() => navigate('/admin')}
          >
            Admin
          </button>
        )}
      </nav>

      <div className="topbar__right">
        <div ref={presenceRef} style={{ position: 'relative' }}>
          <button
            type="button"
            className="presence-chip"
            onClick={() => setPresenceOpen((open) => !open)}
            aria-haspopup="true"
            aria-expanded={presenceOpen}
            title="Presence"
          >
            <span
              className={`presence-chip__dot${
                presence === 'away' ? ' presence-chip__dot--away' : presence === 'off' ? ' presence-chip__dot--off' : ''
              }`}
            />
            {presence === 'on' ? 'On shift' : presence === 'away' ? 'Away' : 'Off'}
            <ChevronDownIcon size={12} />
          </button>
          {presenceOpen && (
            <div className="popover" role="menu">
              {(
                [
                  ['on', 'On shift'],
                  ['away', 'Away'],
                  ['off', 'Off'],
                ] as Array<[Presence, string]>
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className="popover__item"
                  onClick={() => {
                    setPresence(key);
                    setPresenceOpen(false);
                  }}
                  role="menuitem"
                >
                  <span
                    className={`presence-chip__dot${
                      key === 'away' ? ' presence-chip__dot--away' : key === 'off' ? ' presence-chip__dot--off' : ''
                    }`}
                  />
                  {label}
                  {presence === key && (
                    <span style={{ marginLeft: 'auto', color: 'var(--brand)', display: 'inline-flex' }}>
                      <CheckIcon size={14} stroke={2} />
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <span title={connectionTitle}>
          <StatusPill tone={connectionTone} pulsing={connection === 'connecting'}>
            {connectionLabel}
          </StatusPill>
        </span>

        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={onOpenSettings}
          aria-label="Account settings"
          title="Account settings"
          style={{ width: 32, height: 32, padding: 0 }}
        >
          <SettingsIcon size={15} />
        </button>

        <div ref={userRef} style={{ position: 'relative' }}>
          <Avatar name={operatorName} presence={presence} onClick={() => setUserOpen((open) => !open)} />
          {userOpen && (
            <div className="popover" style={{ minWidth: 220 }} role="menu">
              <div className="popover__user">
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{operatorName}</div>
                {operatorEmail && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{operatorEmail}</div>
                )}
                <div style={{ marginTop: 6 }}>
                  <span className="pill pill--teal" style={{ fontSize: 10 }}>
                    <span className="dot" />
                    {operatorRole}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="popover__item"
                onClick={() => {
                  setUserOpen(false);
                  onOpenSettings();
                }}
                role="menuitem"
              >
                <KeyboardIcon size={14} />
                Account & shortcuts
              </button>
              <div className="popover__divider" />
              <button
                type="button"
                className="popover__item popover__item--danger"
                onClick={() => {
                  setUserOpen(false);
                  void logout();
                }}
                role="menuitem"
              >
                <LogOutIcon size={14} />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
